import React, { useState } from 'react';
import '../healthoverview.css';
import { Card, CardContent, Typography } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ChartControls from './ChartControls';
import { useQuery } from 'react-query';
import {
  getVitalsGraphData,
  getRecpientVitalsGraphData,
  getGlucoseGraphData,
  getRecipientGlucoseGraphData,
} from '../../../utils/api';
import CustomTooltip from './CustomTooltip';
import { VITAL_CHART_TYPES, VITAL_CHART_LABELS } from '../../../constants';
import { getFormattedChartPeriodLabel } from '../../../utils';
import { fixedDecimalToTwo } from '../../../utils';

export default function VitalsChart({ userData, recipientId, type }) {
  const userId = userData?._id || userData?.id;
  const [period, setPeriod] = useState('monthly');
  const [showComparison, setShowComparison] = useState(false);
  const chartType = VITAL_CHART_TYPES[type];
  let apiResponse = null;
  const isGlucose = type == 'Glucose';
  const { data: vitalsGraphData } = useQuery({
    queryKey: ['vitalsGraph', userId, recipientId, chartType, period, showComparison, type],
    queryFn: () =>
      recipientId
        ? getRecpientVitalsGraphData({ userId, recipientId, type: chartType, period, showComparison })
        : getVitalsGraphData({ userId, type: chartType, period, showComparison }),
    staleTime: 60000,
    enabled: !isGlucose,
    retry: 2,
  });
  const { data: glucoseGraphData } = useQuery({
    queryKey: ['glucoseGraph', userId, recipientId, period, showComparison, type],
    queryFn: () =>
      recipientId
        ? getRecipientGlucoseGraphData({ userId, recipientId, period, showComparison })
        : getGlucoseGraphData({ userId, period, showComparison }),
    staleTime: 60000,
    enabled: isGlucose,
    retry: 2,
  });
  apiResponse = isGlucose ? glucoseGraphData : vitalsGraphData;

  const getChartData = () => {
    if (!apiResponse?.data || apiResponse.data.length === 0) {
      return [{ period: 'No data available', current: 0, previous: 0 }];
    }

    return apiResponse.data.map((item) => {
      if (chartType === 'BLOOD_PRESSURE') {
        return {
          period: getFormattedChartPeriodLabel(period, item.period),
          systolic: fixedDecimalToTwo(item.systolic),
          diastolic: fixedDecimalToTwo(item.diastolic),
          previousSystolic: fixedDecimalToTwo(item.prevSystolic),
          previousDiastolic: fixedDecimalToTwo(item.prevDiastolic),
        };
      } else {
        return {
          period: getFormattedChartPeriodLabel(period, item.period),
          current: fixedDecimalToTwo(item.data),
          previous: fixedDecimalToTwo(item.prevData),
        };
      }
    });
  };

  const chartData = getChartData();

  const getCurrentValues = () => {
    if (chartType === 'BLOOD_PRESSURE') {
      return chartData.map((data) => parseFloat(data.systolic)).filter((val) => val > 0);
    }
    return chartData.map((data) => parseFloat(data.current)).filter((val) => val > 0);
  };

  const chartValues = getCurrentValues();
  const avgValue = chartValues.length
    ? (chartValues.reduce((sum, val) => sum + val, 0) / chartValues.length).toFixed(2)
    : 'N/A';

  const highestValue = chartValues.length ? Number(Math.max(...chartValues)).toFixed(2) : 'N/A';
  const lowestValue = chartValues.length ? Number(Math.min(...chartValues)).toFixed(2) : 'N/A';
  const { label, unit } = VITAL_CHART_LABELS[chartType] || {};

  const getYAxisDomain = () => {
    if (chartType === 'BLOOD_PRESSURE') {
      return [0, 200];
    }
    if (chartType === 'CALORIES_BURNED') {
      return [0, 1200];
    }
    if (chartType === 'HEART_RATE') {
      return [0, 180];
    }
    if (chartType === 'GLUCOSE_LEVELS') {
      return [0, 200];
    }
    return undefined;
  };

  return (
    <div className="vitals-graph-container">
      <ChartControls
        period={period}
        handlePeriodChange={(e) => setPeriod(e.target.value)}
        showComparison={showComparison}
        toggleComparison={() => setShowComparison(!showComparison)}
        dataType={type}
        userId={userId}
        selectedRecipient={recipientId}
      />

      <Card className="stats-card">
        <CardContent className="stats-content">
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Avg {label}
            </Typography>
            <Typography variant="h6" component="p" className="stat-value">
              {avgValue} {unit}
            </Typography>
          </div>
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Highest {label}
            </Typography>
            <Typography variant="subtitle1" component="p" className="stat-value">
              {highestValue} {unit}
            </Typography>
          </div>
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Lowest {label}
            </Typography>
            <Typography variant="subtitle1" component="p" className="stat-value">
              {lowestValue} {unit}
            </Typography>
          </div>
        </CardContent>
      </Card>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 10, right: 30, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="period" />
            <YAxis domain={getYAxisDomain()} />
            <Tooltip content={<CustomTooltip />} cursor={false} />

            {chartType === 'BLOOD_PRESSURE' ? (
              <>
                <Line
                  type="monotone"
                  dataKey="systolic"
                  stroke="#e74c3c"
                  strokeWidth={2}
                  dot={false}
                  name="Systolic"
                  unit={unit}
                />
                <Line
                  type="monotone"
                  dataKey="diastolic"
                  stroke="#95caf9"
                  strokeWidth={2}
                  dot={false}
                  name="Diastolic"
                  unit={unit}
                />
                {showComparison && (
                  <>
                    <Line
                      type="monotone"
                      dataKey="previousSystolic"
                      stroke="#ffa500"
                      strokeWidth={2}
                      dot={false}
                      name="Previous Systolic"
                      unit={unit}
                    />
                    <Line
                      type="monotone"
                      dataKey="previousDiastolic"
                      stroke="#4b0082"
                      strokeWidth={2}
                      dot={false}
                      name="Previous Diastolic"
                      unit={unit}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <Line
                  type="monotone"
                  dataKey="current"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  name="Current"
                  unit={unit}
                />
                {showComparison && (
                  <Line
                    type="monotone"
                    dataKey="previous"
                    stroke="#94a3b8"
                    strokeWidth={2}
                    dot={false}
                    name="Previous"
                    unit={unit}
                  />
                )}
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
